import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';

import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { PageHeader } from '../PageHeader';
import type { CoreExpectedWebhookStatus } from './coreConfigurationApi';
import {
  canProvisionToken,
  canProvisionWebhooks,
  canReactivateWebhooks,
  formatCoreConfigurationCount,
  getWebhookChipPresentation,
} from './coreConfigurationPresentation';
import { useCoreConfiguration, type UseCoreConfigurationOptions } from './useCoreConfiguration';

export type CoreConfigurationPageProps = UseCoreConfigurationOptions;

const UnavailableCount = () => (
  <Typography aria-label="Not available" component="span" variant="body2">
    —
  </Typography>
);

const MetadataRow = ({ label, value }: { label: string; value: number | null | undefined }) => {
  const formatted = formatCoreConfigurationCount(value);

  return (
    <Typography variant="body2">
      {label}: {formatted ?? <UnavailableCount />}
    </Typography>
  );
};

const WebhookRow = ({ webhook }: { webhook: CoreExpectedWebhookStatus }) => {
  const chip = getWebhookChipPresentation(webhook);

  return (
    <Stack component="article" aria-label={webhook.trigger} spacing={0.75} sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {webhook.trigger}
        </Typography>
        <Chip color={chip.color} label={chip.label} size="small" variant="filled" />
      </Box>
      {webhook.configured && (
        <Stack spacing={0.25}>
          <MetadataRow label="Consecutive failures" value={webhook.consecutiveFailures} />
          <MetadataRow label="Successful requests" value={webhook.totalSuccessfulRequests} />
          <MetadataRow label="Failed requests" value={webhook.totalFailedRequests} />
          <Typography variant="body2">Target: {webhook.expectedTarget}</Typography>
        </Stack>
      )}
    </Stack>
  );
};

export const CoreConfigurationPage = (props: CoreConfigurationPageProps) => {
  const configuration = useCoreConfiguration(props);
  const status = configuration.status;
  const busy = configuration.loading || configuration.mutationInFlight !== null;

  return (
    <>
      <PageHeader
        title="Core Configuration"
        subtitle="Manage the Core API Token and webhook configuration"
      />
      <Box aria-busy={busy} aria-label="Core configuration" component="section">
        {configuration.loading ? (
          <LoadingState message="Checking Core configuration…" />
        ) : configuration.errorMessage !== null ? (
          <ErrorState message={configuration.errorMessage} onRetry={configuration.reload} />
        ) : status !== null ? (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
                      API access token
                    </Typography>
                    <Chip
                      color={status.tokenValid ? 'success' : 'error'}
                      label={status.tokenValid ? 'Valid' : 'Invalid'}
                      size="small"
                      variant="filled"
                    />
                  </Box>
                  {!status.tokenValid && status.envTokenInUse && (
                    <Typography variant="body2" color="text.secondary">
                      The environment Direct Platform token is invalid. Rotate `DIRECT_PLATFORM_API_TOKEN` rather than
                      minting a dashboard token.
                    </Typography>
                  )}
                  {!status.tokenValid && (
                    <Typography variant="body2">
                      Fix the API access token before webhook status can be checked.
                    </Typography>
                  )}
                  {canProvisionToken(status) && (
                    <Box>
                      <Button
                        aria-busy={configuration.mutationInFlight === 'provisionToken' || undefined}
                        disabled={configuration.mutationInFlight !== null}
                        onClick={() => void configuration.provisionToken()}
                        variant="contained"
                      >
                        Provision new token
                      </Button>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
            {status.tokenValid && (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
                      Webhooks
                    </Typography>
                    {status.webhooks.map(webhook => (
                      <WebhookRow key={webhook.trigger} webhook={webhook} />
                    ))}
                    {(canProvisionWebhooks(status) || canReactivateWebhooks(status)) && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {canProvisionWebhooks(status) && (
                          <Button
                            aria-busy={configuration.mutationInFlight === 'provisionWebhooks' || undefined}
                            disabled={configuration.mutationInFlight !== null}
                            onClick={() => void configuration.provisionWebhooks()}
                            variant="contained"
                          >
                            Provision webhooks
                          </Button>
                        )}
                        {canReactivateWebhooks(status) && (
                          <Button
                            aria-busy={configuration.mutationInFlight === 'reactivateWebhooks' || undefined}
                            disabled={configuration.mutationInFlight !== null}
                            onClick={() => void configuration.reactivateWebhooks()}
                            variant="contained"
                          >
                            Re-activate webhooks
                          </Button>
                        )}
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        ) : null}
        <Box
          aria-atomic="true"
          aria-live="polite"
          role="status"
          sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
        >
          {configuration.announcement}
        </Box>
      </Box>
    </>
  );
};
